'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';

const accountDeletionSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  username: z.string().min(1, 'Please enter your username or account ID'),
  phoneNumber: z.string().optional(),
  reason: z.string().min(1, 'Please select a reason for deletion'),
  additionalInfo: z.string().optional(),
  confirmDeletion: z.boolean().refine((val) => val === true, {
    message: 'You must confirm that you understand this action is irreversible',
  }),
});

type AccountDeletionFormData = z.infer<typeof accountDeletionSchema>;

const deletionReasons = [
  { value: '', label: 'Select a reason' },
  { value: 'privacy-concerns', label: 'Privacy concerns' },
  { value: 'not-using-app', label: 'No longer using the app' },
  { value: 'found-alternative', label: 'Found an alternative app' },
  { value: 'too-many-notifications', label: 'Too many notifications' },
  { value: 'technical-issues', label: 'Technical issues with the app' },
  { value: 'account-security', label: 'Account security concerns' },
  { value: 'data-storage', label: 'Concerns about data storage' },
  { value: 'other', label: 'Other reason' },
];

export function ZubeneSupportForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AccountDeletionFormData>({
    resolver: zodResolver(accountDeletionSchema),
    defaultValues: {
      confirmDeletion: false,
    },
  });

  const selectedReason = watch('reason');

  const onSubmit = async (data: AccountDeletionFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const functionUrl = process.env.NEXT_PUBLIC_ACCOUNT_DELETION_FUNCTION_URL || '/api/account-deletion';
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          app: 'Zubene',
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Email Field */}
      <div className="mb-12">
        <label className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 block">
          Email Address *
        </label>
        <input
          {...register('email')}
          type="email"
          placeholder="Enter your registered email"
          className={cn(
            'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/50',
            errors.email ? 'border-red-500' : 'border-white/30 focus:border-white'
          )}
        />
        {errors.email && (
          <p className="mt-2 text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>

      {/* Two column layout for Username and Phone */}
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-12 mb-12">
        {/* Username / Account ID Field */}
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 block">
            Username / Account ID *
          </label>
          <input
            {...register('username')}
            type="text"
            placeholder="Your username or account ID"
            className={cn(
              'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/50',
              errors.username ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
          />
          {errors.username && (
            <p className="mt-2 text-sm text-red-500">{errors.username.message}</p>
          )}
        </div>

        {/* Phone Number Field (Optional) */}
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 block">
            Phone Number (Optional)
          </label>
          <input
            {...register('phoneNumber')}
            type="tel"
            placeholder="Your registered phone number"
            className={cn(
              'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/50',
              'border-white/30 focus:border-white'
            )}
          />
        </div>
      </div>

      {/* Reason Selection - Full Width */}
      <div className="mb-12">
        <label className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 block">
          Reason for Deletion *
        </label>
        <select
          {...register('reason')}
          className={cn(
            'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors appearance-none cursor-pointer',
            errors.reason ? 'border-red-500' : 'border-white/30 focus:border-white',
            'bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'white\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_0_center] bg-[length:24px]'
          )}
          style={{ backgroundColor: 'transparent' }}
        >
          {deletionReasons.map((reason) => (
            <option
              key={reason.value}
              value={reason.value}
              style={{ backgroundColor: '#173835', color: 'white' }}
            >
              {reason.label}
            </option>
          ))}
        </select>
        {errors.reason && (
          <p className="mt-2 text-sm text-red-500">{errors.reason.message}</p>
        )}
      </div>

      {/* Additional Information - Full Width */}
      <div className="mb-12">
        <label className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 block">
          Additional Information {selectedReason === 'other' ? '*' : '(Optional)'}
        </label>
        <textarea
          {...register('additionalInfo')}
          rows={4}
          placeholder="Please provide any additional details that may help us process your request..."
          className={cn(
            'w-full resize-none border-b bg-transparent py-4 text-lg text-white outline-none transition-colors placeholder:text-white/50',
            'border-white/30 focus:border-white'
          )}
        />
      </div>

      {/* Confirmation Checkbox */}
      <div className="mb-12">
        <label className="flex items-start gap-4 cursor-pointer group">
          <div className="relative mt-1">
            <input
              {...register('confirmDeletion')}
              type="checkbox"
              className="peer sr-only"
            />
            <div className={cn(
              'w-6 h-6 border-2 rounded transition-colors',
              errors.confirmDeletion ? 'border-red-500' : 'border-white/30 group-hover:border-white/60',
              'peer-checked:bg-white peer-checked:border-white'
            )}>
              <svg
                className="w-full h-full text-[#173835] opacity-0 peer-checked:opacity-100 transition-opacity"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <span className="text-white/80 text-base leading-relaxed">
            I understand that this action is <strong className="text-white">permanent and irreversible</strong>.
            All my data, including account information, preferences, and activity history will be permanently deleted
            and cannot be recovered.
          </span>
        </label>
        {errors.confirmDeletion && (
          <p className="mt-3 text-sm text-red-500">{errors.confirmDeletion.message}</p>
        )}
      </div>

      {/* Submit Button - Centered */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '48px' }}>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            border: '1px solid #fff',
            backgroundColor: 'transparent',
            paddingLeft: '60px',
            paddingRight: '60px',
            paddingTop: '20px',
            paddingBottom: '20px',
            fontSize: '16px',
            fontWeight: '500',
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minWidth: '280px',
          }}
          className="hover:bg-white hover:text-[#173835] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting Request...' : 'Submit Deletion Request'}
        </button>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="mt-10 p-6 rounded-lg bg-green-500/10 border border-green-500/30">
          <div className="flex items-start gap-4">
            <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-green-400 font-semibold mb-1">Request Submitted Successfully</h4>
              <p className="text-green-400/80">
                Your account deletion request has been received. You will receive a confirmation email within 48 hours once your account has been deleted.
              </p>
            </div>
          </div>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="mt-10 p-6 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-start gap-4">
            <svg className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-red-400 font-semibold mb-1">Submission Failed</h4>
              <p className="text-red-400/80">
                Something went wrong. Please try again or contact us directly at{' '}
                <a href="mailto:info@datacode.app" className="underline hover:text-red-300">
                  info@datacode.app
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
